import { parseSizeList } from "@/lib/manufacturing/groupMembers";
import { normalizeSeriesCode, seriesCodeFromName } from "@/lib/manufacturing/seriesWizardPlan";
import { composeComponentTags, composeProductTags } from "./composeTags";
import { resolveSlotFill, slugFillDescription } from "./predicates";
import {
  applyNamingPattern,
  DEFAULT_CODE_PATTERN,
  DEFAULT_NAME_PATTERN,
  styleDisplayKey,
} from "./skuPattern";
import type {
  ComposeArchetype,
  ComposeNode,
  ComposePlan,
  ComposeSlotDef,
} from "./types";

export { applyNamingPattern } from "./skuPattern";

let _seq = 0;
function nextId(prefix: string): string {
  _seq += 1;
  return `${prefix}-${_seq}-${Math.random().toString(36).slice(2, 7)}`;
}

/** Reset id counter (tests). */
export function resetComposeIdSequence(): void {
  _seq = 0;
}

function componentCode(
  seriesCode: string,
  slot: ComposeSlotDef,
  sizeLabel: string | null,
  styleKey: string | null,
  variantKey: string | null,
): string {
  const parts = [seriesCode, slot.key.toUpperCase().replace(/_/g, "-")];
  if (slot.sizeSpecific && sizeLabel) parts.push(sizeLabel);
  if (slot.styleSpecific && styleKey) parts.push(styleKey.toUpperCase());
  if (variantKey) parts.push(variantKey.toUpperCase());
  return parts.join("-");
}

function componentName(
  seriesName: string,
  slot: ComposeSlotDef,
  sizeLabel: string | null,
  styleKey: string | null,
  fillDescription: string,
  variantKey: string | null,
): string {
  const bits = [seriesName, slot.label];
  if (slot.sizeSpecific && sizeLabel) bits.push(sizeLabel);
  if (slot.styleSpecific && styleKey) bits.push(styleKey);
  if (fillDescription) bits.push(fillDescription);
  else if (variantKey) bits.push(variantKey);
  return bits.join(" ");
}

type SharedKey = string;

function sharedKey(
  slot: ComposeSlotDef,
  seriesCode: string,
  sizeLabel: string | null,
  styleKey: string | null,
  variantKey: string | null,
): SharedKey {
  return [
    slot.key,
    slot.seriesSpecific ? seriesCode : "",
    slot.sizeSpecific ? sizeLabel ?? "" : "",
    slot.styleSpecific ? styleKey ?? "" : "",
    variantKey ?? "",
  ].join("|");
}

/**
 * Expand bottom-up archetype into product nodes + shared component DAG.
 * Replaces plan.nodes.
 */
export function expandBottomUp(plan: ComposePlan): ComposePlan {
  const a = plan.archetype;
  const seriesCode =
    normalizeSeriesCode(a.seriesCode) || seriesCodeFromName(a.seriesName) || "SERIES";
  const seriesName = a.seriesName.trim() || seriesCode;
  const sizes = parseSizeList(a.sizesRaw);
  const styles = a.styles.map((s) => s.trim()).filter(Boolean);
  if (sizes.length === 0 || styles.length === 0) {
    return { ...plan, nodes: [], updatedAt: new Date().toISOString() };
  }

  const shared = new Map<SharedKey, ComposeNode>();
  const products: ComposeNode[] = [];

  const codePattern = a.namingPattern.trim() || DEFAULT_CODE_PATTERN;
  const namePattern = (a.namePattern ?? "").trim() || DEFAULT_NAME_PATTERN;

  for (const sizeLabel of sizes) {
    for (const styleKey of styles) {
      const styleLabel = styleDisplayKey(styleKey);
      const patternVars = {
        CODE: seriesCode,
        SIZE: sizeLabel,
        STYLE: styleKey,
        SERIES: seriesName,
      };
      const code = applyNamingPattern(codePattern, patternVars);
      const name = applyNamingPattern(namePattern, patternVars);
      const slotLinks: ComposeNode["slots"] = [];

      for (const slot of a.slots) {
        const { fillDescription, variantKey } = resolveSlotFill(sizeLabel, slot);
        const styleForShared = slot.styleSpecific ? styleKey : null;
        const sizeForShared = slot.sizeSpecific ? sizeLabel : null;
        const key = sharedKey(slot, seriesCode, sizeForShared, styleForShared, variantKey);
        let child = shared.get(key);
        if (!child) {
          child = {
            id: nextId("cmp"),
            role: "component",
            kind: slot.key === "finish_package" ? "package" : "assembly",
            code: componentCode(seriesCode, slot, sizeForShared, styleForShared, variantKey),
            name: componentName(
              seriesName,
              slot,
              sizeForShared,
              styleForShared ? styleDisplayKey(styleForShared) : null,
              fillDescription,
              variantKey,
            ),
            sizeLabel: sizeForShared,
            styleKey: styleForShared ? styleDisplayKey(styleForShared) : null,
            slotKey: slot.key,
            variantKey,
            tags: composeComponentTags({
              sizeLabel: sizeForShared,
              styleKey: styleForShared,
              slotKey: slot.key,
              variantKey,
              fillSlug: fillDescription
                ? slugFillDescription(fillDescription) || fillDescription
                : null,
            }),
            slots: [],
            match: { status: "unmatched" },
          };
          shared.set(key, child);
        }
        slotLinks.push({
          slotKey: slot.key,
          label: slot.label,
          childNodeId: child.id,
        });
      }

      products.push({
        id: nextId("prd"),
        role: "product",
        kind: "product",
        code,
        name,
        sizeLabel,
        styleKey: styleLabel,
        slotKey: null,
        variantKey: null,
        tags: composeProductTags(sizeLabel, styleLabel),
        slots: slotLinks,
        match: { status: "unmatched" },
      });
    }
  }

  return {
    ...plan,
    nodes: [...shared.values(), ...products],
    updatedAt: new Date().toISOString(),
  };
}

export function stylesFromRaw(raw: string): string[] {
  return raw
    .split(/[,;\n]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export function withArchetypeStyles(
  archetype: ComposeArchetype,
  stylesRaw: string,
): ComposeArchetype {
  return { ...archetype, styles: stylesFromRaw(stylesRaw) };
}
